import React from 'react';
import { observer, inject } from 'mobx-react';
import qs from 'qs';
import Pagination from 'components/Pagination';
import Footer from 'components/Footer';
import { currentTime } from 'helpers/time';
import { currencyFormattedAmount } from 'helpers/currency';
import * as DataList from 'components/DataList';
import { PROJECT_COLUMNS, PROJECT_CARDS } from 'components/Tables/Projects';

// A client's projects (client page, Projects tab), laid out like the
// Projects list (components/Tables/Projects) minus the client column.
const COLUMNS = PROJECT_COLUMNS.filter((column) => column.area !== 'client');

const CARDS = {
  ...PROJECT_CARDS,
  areas: PROJECT_CARDS.areas.filter((row) => !/\bclient\b/.test(row)),
};

const MIN_TABLE_WIDTH = 820;

const STATUS_TONES = {
  active: 'blue',
  in_progress: 'blue',
  completed: 'green',
  invoiced: 'purple',
  paused: 'amber',
  cancelled: 'red',
  canceled: 'red',
};

const statusLabel = (status) =>
  String(status || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const dateRange = (start, end) => {
  if (!start && !end) return null;
  const from = start ? currentTime(start) : null;
  const to = end ? currentTime(end) : null;
  if (from && to) {
    const sameYear = from.isSame(to, 'year');
    if (from.isSame(to, 'day')) return from.format('MMM D, YYYY');
    return `${from.format(sameYear ? 'MMM D' : 'MMM D, YYYY')} – ${to.format('MMM D, YYYY')}`;
  }
  return (from || to).format('MMM D, YYYY');
};

const timeRange = (start, end) => {
  const parts = [start, end].filter(Boolean).map((date) => currentTime(date).format('h:mm A'));
  return parts.join(' – ');
};

@inject('viewStore', 'projectStore') @observer
class ProjectsTable extends React.Component {

  componentDidMount() {
    this.props.viewStore.clientProjectSearch.where.client_id = this.props.client.attributes.id;
    this.props.projectStore.loadClientProjects();
  }

  onPaginationChange(newPage) {
    const { viewStore } = this.props;
    viewStore.clientProjectSearch = {
      ...viewStore.clientProjectSearch,
      page: newPage,
    };
    this.props.projectStore.loadClientProjects();
  }

  showLink(project) {
    const { viewStore } = this.props;
    return `/dashboard/projects/${project.id}/show?${qs.stringify(viewStore.projectShowSearch, { encode: false })}`;
  }

  render() {
    const {
      viewStore,
      projectStore: {
        clientProjects: { objects, isLoading, isError, meta },
      },
    } = this.props;

    if (isLoading && objects.length === 0) {
      return (
        <DataList.Loading columns={COLUMNS} cards={CARDS} minTableWidth={MIN_TABLE_WIDTH} noun="projects" rows={5} />
      );
    }
    if (isError) {
      return (
        <DataList.LoadError
          noun="projects"
          onRetry={() => this.onPaginationChange((meta && meta.page) || 1)}
        />
      );
    }
    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="projects"
          message="Projects you create for this client will show up here."
        />
      );
    }

    const pagination = meta && meta.total_pages > 1 && (
      <Footer>
        <div className="bp3-navbar-group bp3-align-left">
          <Pagination value={meta.page} total={meta.total_pages} onChange={this.onPaginationChange.bind(this)} />
        </div>
      </Footer>
    );

    return (
      <React.Fragment>
        <DataList.List
          preferTable
          columns={COLUMNS}
          cards={CARDS}
          minTableWidth={MIN_TABLE_WIDTH}
          total={(meta && meta.total_records) || objects.length}
          noun="projects"
          note={DataList.sortNote(viewStore.clientProjectSearch.order)}
        >
          {objects.map((project) => {
            const attributes = project.attributes;
            const status = attributes.status;
            const dates = dateRange(attributes.start_date, attributes.end_date);
            const href = this.showLink(project);
            return (
              <DataList.Row key={project.id}>
                <DataList.Cell area="project">
                  <DataList.Lines
                    primary={
                      <DataList.RowLink
                        href={href}
                        label={`Project ${attributes.number || ''}: ${attributes.name || ''}`}
                      >
                        {attributes.number ? `#${attributes.number}` : 'Project'}
                      </DataList.RowLink>
                    }
                    secondary={attributes.name}
                  />
                </DataList.Cell>
                <DataList.Cell area="dates">
                  {dates ? (
                    <DataList.Lines
                      primary={dates}
                      secondary={timeRange(attributes.start_date, attributes.end_date)}
                      strong={false}
                    />
                  ) : (
                    <DataList.Muted>No dates</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="po">
                  {attributes.po_number ? (
                    <DataList.Lines
                      primary={<React.Fragment><DataList.CardOnly>PO </DataList.CardOnly>{attributes.po_number}</React.Fragment>}
                      title={attributes.po_number}
                      strong={false}
                    />
                  ) : (
                    <DataList.Muted>No PO</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="price">
                  <div style={{ marginLeft: 'auto' }}>
                    {attributes.price != null ? (
                      <DataList.Lines primary={currencyFormattedAmount(attributes.price)} />
                    ) : (
                      <DataList.Muted>No price</DataList.Muted>
                    )}
                  </div>
                </DataList.Cell>
                <DataList.Cell area="status">
                  {status && (
                    <DataList.Pill tone={STATUS_TONES[status] || 'grey'}>{statusLabel(status)}</DataList.Pill>
                  )}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      { icon: 'eye-open', label: 'View project', href },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        {pagination}
      </React.Fragment>
    );
  }
}

export default ProjectsTable;
